/* eslint-disable no-case-declarations */
/* eslint-disable import/prefer-default-export */
import { LoadStatus } from '../../Types';
import { BusinessActions, BusinessActionTypes } from './actions';
import { BusinessState } from './Types/BusinessState';
import { Occupant } from './Types/Occupant';
import { Role } from './Types/Role';

const initialState: BusinessState = {
    occupants: {
        loadStatus: LoadStatus.INITIAL_STATE,
        sortOrder: [],
    },
    businessUsers: {},
    roles: {},
};

export function businessReducer(state: BusinessState = initialState, action: BusinessActionTypes): BusinessState {
    switch (action.type) {
        case BusinessActions.LOAD_BUSINESS_OCCUPANT:
            return {
                ...state,
                occupants: {
                    ...state.occupants,
                    [action.payload]: {
                        ...state.occupants[action.payload],
                        loadStatus: LoadStatus.PENDING,
                    },
                },
            };
        case BusinessActions.SET_BUSINESS_OCCUPANT:
            return {
                ...state,
                occupants: {
                    ...state.occupants,
                    [action.payload.id]: {
                        ...action.payload,
                    },
                },
            };
        case BusinessActions.SET_BUSINESS_OCCUPANT_ERROR_STATE:
            return {
                ...state,
                occupants: {
                    ...state.occupants,
                    [action.payload.occupantId]: {
                        ...state.occupants[action.payload.occupantId],
                        loadStatus: LoadStatus.ERROR,
                        errorState: { error: action.payload.errorMessage },
                    },
                },
            };
        case BusinessActions.LOAD_BUSINESS_OCCUPANTS:
            return {
                ...state,
                occupants: {
                    ...state.occupants,
                    loadStatus: LoadStatus.PENDING,
                },
            };
        case BusinessActions.SET_BUSINESS_OCCUPANTS:
            return {
                ...state,
                occupants: {
                    loadStatus: LoadStatus.LOADED,
                    sortOrder: action.payload.map(({ id }: Occupant) => id),
                    ...action.payload.reduce(
                        (agg, curr: Occupant) => ({
                            ...agg,
                            [curr.id]: curr,
                        }),
                        {},
                    ),
                },
            };
        case BusinessActions.SET_BUSINESS_OCCUPANTS_ERROR_STATE:
            return {
                ...state,
                occupants: {
                    loadStatus: LoadStatus.ERROR,
                    sortOrder: [],
                    errorState: action.payload.secondaryMessage
                        ? {
                              error: action.payload.errorMessage,
                              secondaryMessage: action.payload.secondaryMessage,
                          }
                        : { error: action.payload.errorMessage },
                },
            };
        case BusinessActions.UPDATE_BUSINESS_OCCUPANT_MAILING_ADDRESS:
            if (!Object.hasOwnProperty.call(state.occupants, action.payload.occupantId)) return state;

            return {
                ...state,
                occupants: {
                    ...state.occupants,
                    [action.payload.occupantId]: {
                        ...state.occupants[action.payload.occupantId],
                        mailingAddress: action.payload.address,
                    },
                },
            };
        case BusinessActions.LOAD_BUSINESS_USERS_FOR_OCCUPANT:
            return {
                ...state,
                businessUsers: {
                    ...state.businessUsers,
                    [action.payload]: {
                        loadStatus: LoadStatus.PENDING,
                        sortOrder: [],
                    },
                },
            };
        case BusinessActions.SET_BUSINESS_USERS_FOR_OCCUPANT:
            return {
                ...state,
                businessUsers: {
                    ...state.businessUsers,
                    [action.payload.occupantId]: {
                        loadStatus: LoadStatus.LOADED,
                        sortOrder: action.payload.users.map(({ id }) => id),
                        ...action.payload.users.reduce(
                            (agg, curr) => ({
                                ...agg,
                                [curr.id]: curr,
                            }),
                            {},
                        ),
                    },
                },
            };
        case BusinessActions.SET_BUSINESS_USERS_FOR_OCCUPANT_ERROR_STATE:
            return {
                ...state,
                businessUsers: {
                    ...state.businessUsers,
                    [action.payload.occupantId]: {
                        loadStatus: LoadStatus.ERROR,
                        sortOrder: [],
                        errorState: { error: action.payload.errorMessage },
                    },
                },
            };
        case BusinessActions.ADD_BUSINESS_USER_ROLE:
            if (!Object.hasOwnProperty.call(state.businessUsers, action.payload.occupantId)) return state;

            if (!Object.hasOwnProperty.call(state.businessUsers[action.payload.occupantId], action.payload.userId)) {
                return state;
            }

            return {
                ...state,
                businessUsers: {
                    ...state.businessUsers,
                    [action.payload.occupantId]: {
                        ...state.businessUsers[action.payload.occupantId],
                        [action.payload.userId]: {
                            ...state.businessUsers[action.payload.occupantId][action.payload.userId],
                            roles: [
                                ...state.businessUsers[action.payload.occupantId][action.payload.userId].roles,
                                action.payload.roleId,
                            ].filter((role, index, self) => self.indexOf(role) === index),
                        },
                    },
                },
            };
        case BusinessActions.REMOVE_BUSINESS_USER_ROLE:
            if (!Object.hasOwnProperty.call(state.businessUsers, action.payload.occupantId)) return state;

            if (!Object.hasOwnProperty.call(state.businessUsers[action.payload.occupantId], action.payload.userId)) {
                return state;
            }

            return {
                ...state,
                businessUsers: {
                    ...state.businessUsers,
                    [action.payload.occupantId]: {
                        ...state.businessUsers[action.payload.occupantId],
                        [action.payload.userId]: {
                            ...state.businessUsers[action.payload.occupantId][action.payload.userId],
                            roles: state.businessUsers[action.payload.occupantId][action.payload.userId].roles.filter(
                                (role) => role !== action.payload.roleId,
                            ),
                        },
                    },
                },
            };
        case BusinessActions.REMOVE_BUSINESS_USER:
            if (!Object.hasOwnProperty.call(state.businessUsers, action.payload.occupantId)) return state;

            if (!Object.hasOwnProperty.call(state.businessUsers[action.payload.occupantId], action.payload.userId)) {
                return state;
            }

            const { [action.payload.userId]: rbuTemp, ...rbuTempUsers } = state.businessUsers[action.payload.occupantId];

            return {
                ...state,
                businessUsers: {
                    ...state.businessUsers,
                    [action.payload.occupantId]: {
                        ...rbuTempUsers,
                        sortOrder:
                            state.businessUsers[action.payload.occupantId].sortOrder?.filter((i) => rbuTemp.id !== i) || [],
                    },
                },
            };
        case BusinessActions.LOAD_BUSINESS_ROLES:
            return {
                ...state,
                roles: {
                    ...state.roles,
                    [action.payload]: {
                        loadStatus: LoadStatus.PENDING,
                    },
                },
            };
        case BusinessActions.SET_BUSINESS_ROLES:
            return {
                ...state,
                roles: {
                    ...state.roles,
                    [action.payload.occupantId]: {
                        loadStatus: LoadStatus.LOADED,
                        sortOrder: action.payload.roles.map(({ id }: Role) => id),
                        ...action.payload.roles.reduce(
                            (agg, curr: Role) => ({
                                ...agg,
                                [curr.id]: curr,
                            }),
                            {},
                        ),
                    },
                },
            };
        case BusinessActions.SET_BUSINESS_ROLES_ERROR_STATE:
            return {
                ...state,
                roles: {
                    ...state.roles,
                    [action.payload.occupantId]: {
                        loadStatus: LoadStatus.ERROR,
                        sortOrder: [],
                        errorState: { error: action.payload.errorMessage },
                    },
                },
            };
        default:
            return {
                ...state,
            };
    }
}
